import React, { useEffect, useRef, useState } from 'react';
import { Slider } from '../ui/slider';
import { Sun, Snowflake, Maximize2, Thermometer } from 'lucide-react';
import { theme } from '../../utils/theme';
import { useLanguage } from '../../contexts/LanguageContext';

export const FilterAnimationImproved = ({ systemActive, onToggle }) => {
  const { t } = useLanguage();
  const canvasRef = useRef(null);
  const [filterDeployment, setFilterDeployment] = useState(80);
  const [mode, setMode] = useState('summer');
  const animationRef = useRef(null);
  const raysRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;

    const isSummer = mode === 'summer';
    
    // Window geometry
    const windowLeft = width * 0.46;
    const windowWidth = width * 0.16;
    const windowTop = height * 0.25;
    const windowBottom = height * 0.92;
    const windowHeight = windowBottom - windowTop;
    const perspectiveDepth = 80;
    const perspectiveY = 25;
    const filterX = windowLeft + perspectiveDepth / 2;

    // Ray colors
    const uvColor = { r: 255, g: 220, b: 60 };
    const irColor = { r: 255, g: 90, b: 70 };
    const rayColor = isSummer ? uvColor : irColor;

    // Initialize rays - spread across the entire visible path for immediate animation
    const initRays = () => {
      const rays = [];
      const numRays = 40;
      
      for (let i = 0; i < numRays; i++) {
        const yPos = windowTop + 20 + (i / numRays) * (windowHeight - 40);
        // MODIFICATION 1 : Longueur des rayons réduite de moitié (25 + random * 15 au lieu de 50 + random * 30)
        const rayLength = 25 + Math.random() * 15;
        
        // Spread rays across the visible travel path
        const spreadPosition = (i / numRays); // 0 to 1
        
        let xPos;
        if (isSummer) {
          // Summer: rays travel left to right
          // Start from rayLength (fully visible) to filterX
          xPos = rayLength + spreadPosition * (filterX - rayLength);
        } else {
          // Winter: rays travel right to left
          // Start from width-rayLength to filterX
          xPos = (width - rayLength) - spreadPosition * (width - rayLength - filterX);
        }
        
        rays.push({
          y: yPos,
          x: xPos,
          speed: 1.5 + Math.random() * 1.0, // Slower speed for more visible rays
          // MODIFICATION 2 : Opacité réduite pour rendre les rayons plus transparents (0.4 + random * 0.2 au lieu de 0.8 + random * 0.2)
          opacity: 0.4 + Math.random() * 0.2,
          length: rayLength,
          state: 'traveling',
          reflectX: 0,
          reflectProgress: 0,
          originalY: yPos,
          willReflect: false,
          index: i
        });
      }
      return rays;
    };

    // Reset ray
    const resetRay = (ray) => {
      ray.y = windowTop + 20 + Math.random() * (windowHeight - 40);
      ray.originalY = ray.y;
      ray.x = isSummer ? -ray.length : width + ray.length;
      ray.state = 'traveling';
      ray.reflectProgress = 0;
      ray.willReflect = false;
      ray.speed = 2 + Math.random() * 2;
      // MODIFICATION 2 (Reset) : Opacité réduite pour le reset aussi (0.35 + random * 0.25 au lieu de 0.7 + random * 0.3)
      ray.opacity = 0.35 + Math.random() * 0.25;
    };

    // Initialize if needed
    if (raysRef.current.length === 0 || raysRef.current[0]?.mode !== mode) {
      raysRef.current = initRays();
      raysRef.current.forEach(r => r.mode = mode);
    }

    // Calculate filter boundaries
    const filterHeight = (windowHeight * filterDeployment) / 100;
    const filterTop = windowTop;
    const filterBottom = windowTop + filterHeight;

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      // Draw labels
      ctx.fillStyle = theme.colors.textMuted;
      ctx.font = 'bold 14px Outfit';
      ctx.fillText(t.filterDemo.exterior, 30, 35);
      ctx.fillText(t.filterDemo.interior, width - 100, 35);

      // Draw window frame with 3D perspective
      const gradient1 = ctx.createLinearGradient(windowLeft, windowTop, windowLeft + perspectiveDepth, windowTop + perspectiveY);
      gradient1.addColorStop(0, 'rgba(80, 80, 100, 0.2)');
      gradient1.addColorStop(1, 'rgba(60, 60, 80, 0.3)');
      ctx.fillStyle = gradient1;
      ctx.strokeStyle = '#666';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(windowLeft, windowTop);
      ctx.lineTo(windowLeft + perspectiveDepth, windowTop + perspectiveY);
      ctx.lineTo(windowLeft + perspectiveDepth, windowBottom + perspectiveY);
      ctx.lineTo(windowLeft, windowBottom);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // Depth lines
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
      ctx.lineWidth = 1;
      for (let i = 1; i < 4; i++) {
        const offset = (windowHeight / 4) * i;
        ctx.beginPath();
        ctx.moveTo(windowLeft, windowTop + offset);
        ctx.lineTo(windowLeft + perspectiveDepth, windowTop + perspectiveY + offset);
        ctx.stroke();
      }

      // Draw smart filter
      if (systemActive && filterDeployment > 0) {
        const filterGlow = isSummer 
          ? 'rgba(231, 165, 95, 0.15)' 
          : 'rgba(100, 180, 200, 0.15)';
        
        ctx.fillStyle = filterGlow;
        ctx.beginPath();
        ctx.moveTo(windowLeft, filterTop);
        ctx.lineTo(windowLeft + perspectiveDepth, filterTop + perspectiveY);
        ctx.lineTo(windowLeft + perspectiveDepth, filterBottom + perspectiveY);
        ctx.lineTo(windowLeft, filterBottom);
        ctx.closePath();
        ctx.fill();

        const slatSpacing = 7;
        const numSlats = Math.floor(filterHeight / slatSpacing);
        const filterColor = isSummer ? theme.colors.primary : '#5DC4D4';
        
        for (let i = 0; i < numSlats; i++) {
          const y = filterTop + i * slatSpacing;

          const gradient = ctx.createLinearGradient(windowLeft + 5, y, windowLeft + perspectiveDepth - 5, y);
          gradient.addColorStop(0, `${filterColor}66`);
          gradient.addColorStop(0.5, filterColor);
          gradient.addColorStop(1, `${filterColor}66`);
          
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(windowLeft + 5, y);
          ctx.lineTo(windowLeft + perspectiveDepth - 5, y + perspectiveY);
          ctx.stroke();

          ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(windowLeft + 5, y + 1);
          ctx.lineTo(windowLeft + perspectiveDepth - 5, y + perspectiveY + 1);
          ctx.stroke();
        }

        ctx.strokeStyle = isSummer ? theme.colors.accent : '#3AA8B8';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(windowLeft + 5, filterBottom);
        ctx.lineTo(windowLeft + perspectiveDepth - 5, filterBottom + perspectiveY);
        ctx.stroke();
      }

      // Calculate blocking
      const blockPercentage = systemActive ? (isSummer ? 0.98 : 0.80) * (filterDeployment / 100) : 0;
      const totalRays = raysRef.current.length;
      const raysToBlock = Math.floor(totalRays * blockPercentage);

      // Process and draw rays
      raysRef.current.forEach((ray, index) => {
        // Check if ray is in the filter zone and should reflect
        const inFilterZone = ray.y >= filterTop && ray.y <= filterBottom;
        
        if (ray.state === 'traveling') {
          ray.willReflect = systemActive && index < raysToBlock && inFilterZone;
        }

        if (isSummer) {
          // Summer: rays from left to right
          if (ray.state === 'traveling') {
            ray.x += ray.speed;
            
            if (ray.x >= filterX - 20 && ray.willReflect) {
              ray.state = 'reflecting';
              ray.reflectX = ray.x;
              ray.reflectProgress = 0;
            } else if (ray.x > width + ray.length) {
              resetRay(ray);
            }
          } else if (ray.state === 'reflecting') {
            ray.reflectProgress += ray.speed * 1.2;
            ray.x = ray.reflectX - ray.reflectProgress;
            ray.y = ray.originalY + Math.sin(ray.reflectProgress * 0.03) * 15;
            
            if (ray.x < -ray.length) {
              resetRay(ray);
            }
          }
        } else {
          // Winter: rays from right to left
          if (ray.state === 'traveling') {
            ray.x -= ray.speed;
            
            if (ray.x <= filterX + 20 && ray.willReflect) {
              ray.state = 'reflecting';
              ray.reflectX = ray.x;
              ray.reflectProgress = 0;
            } else if (ray.x < -ray.length) {
              resetRay(ray);
            }
          } else if (ray.state === 'reflecting') {
            ray.reflectProgress += ray.speed * 1.2;
            ray.x = ray.reflectX + ray.reflectProgress;
            ray.y = ray.originalY + Math.sin(ray.reflectProgress * 0.03) * 15;
            
            if (ray.x > width + ray.length) {
              resetRay(ray);
            }
          }
        }

        // Draw ray with more visible gradient
        const isReflecting = ray.state === 'reflecting';
        const rayLength = ray.length;
        
        const grd = ctx.createLinearGradient(
          ray.x - rayLength, ray.y, 
          ray.x + rayLength, ray.y
        );
        const alpha = Math.min(1, ray.opacity * (isReflecting ? 1.5 : 1.2));
        grd.addColorStop(0, `rgba(${rayColor.r}, ${rayColor.g}, ${rayColor.b}, 0)`);
        grd.addColorStop(0.15, `rgba(${rayColor.r}, ${rayColor.g}, ${rayColor.b}, ${alpha * 0.6})`);
        grd.addColorStop(0.5, `rgba(${rayColor.r}, ${rayColor.g}, ${rayColor.b}, ${alpha})`);
        grd.addColorStop(0.85, `rgba(${rayColor.r}, ${rayColor.g}, ${rayColor.b}, ${alpha * 0.6})`);
        grd.addColorStop(1, `rgba(${rayColor.r}, ${rayColor.g}, ${rayColor.b}, 0)`);

        if (isReflecting) {
          ctx.shadowColor = `rgba(${rayColor.r}, ${rayColor.g}, ${rayColor.b}, 0.8)`;
          ctx.shadowBlur = 15;
        }

        ctx.strokeStyle = grd;
        ctx.lineWidth = isReflecting ? 5 : 4;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(ray.x - rayLength, ray.y);
        ctx.lineTo(ray.x + rayLength, ray.y);
        ctx.stroke();
        
        ctx.shadowBlur = 0;
      });

      // Draw source indicator
      if (isSummer) {
        // Sun
        const sunX = 60, sunY = 70;
        const sunGrd = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 35);
        sunGrd.addColorStop(0, 'rgba(255, 220, 60, 0.8)');
        sunGrd.addColorStop(0.4, 'rgba(255, 200, 50, 0.3)');
        sunGrd.addColorStop(1, 'rgba(255, 200, 50, 0)');
        ctx.fillStyle = sunGrd;
        ctx.beginPath();
        ctx.arc(sunX, sunY, 35, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.fillStyle = '#FFD700';
        ctx.beginPath();
        ctx.arc(sunX, sunY, 14, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.fillStyle = '#FFD700';
        ctx.font = 'bold 12px Outfit';
        ctx.fillText(t.filterDemo.sun, sunX - 15, sunY + 40);
      } else {
        // Heat source
        const heatX = width - 70, heatY = 70;
        const heatGrd = ctx.createRadialGradient(heatX, heatY, 0, heatX, heatY, 35);
        heatGrd.addColorStop(0, 'rgba(255, 90, 70, 0.8)');
        heatGrd.addColorStop(0.4, 'rgba(255, 70, 50, 0.3)');
        heatGrd.addColorStop(1, 'rgba(255, 70, 50, 0)');
        ctx.fillStyle = heatGrd;
        ctx.beginPath();
        ctx.arc(heatX, heatY, 35, 0, Math.PI * 2);
        ctx.fill();

        // Heat waves
        ctx.strokeStyle = 'rgba(255, 90, 70, 0.8)';
        ctx.lineWidth = 2.5;
        for (let i = 0; i < 3; i++) {
          const waveY = heatY - 15 + i * 12;
          ctx.beginPath();
          ctx.moveTo(heatX - 14, waveY);
          ctx.quadraticCurveTo(heatX - 7, waveY - 6, heatX, waveY);
          ctx.quadraticCurveTo(heatX + 7, waveY + 6, heatX + 14, waveY);
          ctx.stroke();
        }

        ctx.fillStyle = '#FF5A46';
        ctx.font = 'bold 12px Outfit';
        ctx.fillText(t.filterDemo.heat, heatX - 20, heatY + 45);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [systemActive, filterDeployment, mode, t]);

  const effectiveBlockPercent = mode === 'summer' 
    ? Math.round(98 * filterDeployment / 100) 
    : Math.round(80 * filterDeployment / 100);

  return (
    <div className="w-full max-w-5xl mx-auto rounded-3xl" style={{ backgroundColor: '#0a0a0a' }}>
      <div 
        className="relative rounded-3xl overflow-hidden shadow-2xl" 
        style={{ borderColor: theme.colors.border, backgroundColor: '#0a0a0a' }}
      >
        <canvas
          ref={canvasRef}
          className="w-full h-[420px]"
          style={{ display: 'block' }}
        />
        
        {/* MODIFICATION 3 : Indicateur de mode déplacé au centre supérieur */}
        <div className={`absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full backdrop-blur-md border ${
          mode === 'summer' 
            ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400' 
            : 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
        }`}>
          <div className="flex items-center gap-2 text-sm font-bold">
            {mode === 'summer' ? <Sun size={16} /> : <Snowflake size={16} />}
            {mode === 'summer' ? t.filterDemo.summerMode : t.filterDemo.winterMode}
          </div>
        </div>

        {/* Ray type indicator */}
        <div className={`absolute bottom-4 left-4 px-3 py-1.5 rounded-lg backdrop-blur-md border text-xs font-mono ${
          mode === 'summer'
            ? 'bg-yellow-900/30 border-yellow-600/30 text-yellow-300'
            : 'bg-red-900/30 border-red-600/30 text-red-300'
        }`}>
          {mode === 'summer' ? t.filterDemo.uvRays : t.filterDemo.irRays}: {effectiveBlockPercent}% {t.filterDemo.blocked}
        </div>
      </div>

      {/* Controls */}
      <div 
        className="mt-8 p-8 rounded-3xl border backdrop-blur-xl relative overflow-hidden" 
        style={{ 
          backgroundColor: mode === 'summer' ? 'rgba(231, 165, 95, 0.05)' : 'rgba(93, 196, 212, 0.05)', 
          borderColor: mode === 'summer' ? theme.colors.primary : '#5DC4D4',
          borderWidth: 2
        }}
      >
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            background: `radial-gradient(circle at top right, ${mode === 'summer' ? theme.colors.primary : '#5DC4D4'}, transparent 70%)`
          }}
        />

        <div className="relative z-10">
          {/* Mode Switcher */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex rounded-2xl p-1 gap-1" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <button
                onClick={() => setMode('summer')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all duration-300 ${
                  mode === 'summer' 
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black shadow-lg' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Sun size={20} />
                {t.filterDemo.summerMode}
              </button>
              <button
                onClick={() => setMode('winter')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all duration-300 ${
                  mode === 'winter' 
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-black shadow-lg' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Snowflake size={20} />
                {t.filterDemo.winterMode}
              </button>
            </div>
          </div>

          {/* Mode description */}
          <div className={`text-center mb-6 text-sm font-medium ${
            mode === 'summer' ? 'text-yellow-400/80' : 'text-cyan-400/80'
          }`}>
            {mode === 'summer' ? t.filterDemo.summerDesc : t.filterDemo.winterDesc}
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-center">
            {/* Deployment Slider */}
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <label className="flex items-center gap-2 text-lg font-bold" style={{ color: theme.colors.text }}>
                  <Maximize2 size={20} style={{ color: mode === 'summer' ? theme.colors.primary : '#5DC4D4' }} />
                  {t.filterDemo.deployment}
                </label>
                <div 
                  className="px-4 py-2 rounded-full font-bold text-2xl"
                  style={{ 
                    backgroundColor: theme.colors.surface, 
                    color: mode === 'summer' ? theme.colors.primary : '#5DC4D4' 
                  }}
                >
                  {filterDeployment}%
                </div>
              </div>
              
              <Slider
                value={[filterDeployment]}
                onValueChange={(value) => setFilterDeployment(value[0])}
                max={100}
                step={1}
                className="w-full"
                disabled={!systemActive}
              />

              <div className="flex justify-between mt-2 text-xs" style={{ color: theme.colors.textMuted }}>
                <span>0% - {t.filterDemo.open}</span>
                <span>50% - {t.filterDemo.partial}</span>
                <span>100% - {t.filterDemo.closed}</span>
              </div>
            </div>

            {/* Toggle Button */}
            <div className="flex justify-center">
              <button
                onClick={onToggle}
                className={`flex flex-col items-center gap-3 px-8 py-6 rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 shadow-xl ${
                  systemActive ? 'text-black' : 'text-white'
                }`}
                style={{ 
                  backgroundColor: systemActive 
                    ? (mode === 'summer' ? theme.colors.primary : '#5DC4D4')
                    : theme.colors.surfaceHover,
                  boxShadow: systemActive 
                    ? `0 10px 40px ${mode === 'summer' ? theme.colors.primary : '#5DC4D4'}40` 
                    : 'none'
                }}
              >
                {systemActive ? (
                  <>
                    {mode === 'summer' ? <Sun className="w-10 h-10" /> : <Thermometer className="w-10 h-10" />}
                    <span className="text-lg">{t.filterDemo.active}</span>
                  </>
                ) : (
                  <>
                    <Snowflake className="w-10 h-10" />
                    <span className="text-lg">{t.filterDemo.inactive}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Status message */}
          <div 
            className="mt-6 p-4 rounded-xl text-center text-sm font-medium"
            style={{ 
              backgroundColor: systemActive 
                ? (mode === 'summer' ? 'rgba(255, 200, 50, 0.1)' : 'rgba(93, 196, 212, 0.1)')
                : 'rgba(255, 255, 255, 0.05)',
              color: systemActive 
                ? (mode === 'summer' ? '#FFD700' : '#5DC4D4')
                : theme.colors.textMuted,
              borderColor: systemActive 
                ? (mode === 'summer' ? '#FFD70050' : '#5DC4D450')
                : theme.colors.border,
              borderWidth: 1
            }}
          >
            {systemActive
              ? (mode === 'summer' 
                  ? `☀️ ${t.filterDemo.summerActiveMessage.replace('{percent}', effectiveBlockPercent)}`
                  : `❄️ ${t.filterDemo.winterActiveMessage.replace('{percent}', effectiveBlockPercent)}`)
              : `💤 ${t.filterDemo.inactiveMessage}`}
          </div>
        </div>
      </div>
    </div>
  );
};